/**
 * Per-feature AI availability, served by MAIE.Backend at
 * /me/ai-capabilities and consumed via `useAiAvailability('feature')`.
 *
 * This is what lets the EMR stay a usable manual EMR when AI is off, the
 * tenant is out of quota, or the stack is down — components ask "can I
 * offer this AI affordance?" and get back a reason to drive mix-by-reason
 * UX (hide when `disabled`, disable+upsell when `quota_exceeded`, disable+
 * "temporarily unavailable" when `unavailable`).
 *
 * Boot flow mirrors usePlatformFlagsStore:
 *   - Pre-auth: STATIC_FALLBACK (fail OPEN — everything available, reason
 *     'ok'). Failing open matches the backend resolver and avoids hiding
 *     AI on a transient fetch blip; the call-time error path still
 *     degrades if a feature is actually down.
 *   - After auth: App.tsx calls refresh() and the real map takes over.
 */
import { create } from 'zustand';
import {
  AiCapabilities,
  AiFeatureKey,
  AiFeatureStatus,
  AiUnavailableReason,
  aiCapabilitiesApi,
} from '@/services/aiCapabilitiesApi';

const OK: AiFeatureStatus = { available: true, reason: 'ok' };

const STATIC_FALLBACK: AiCapabilities = {
  stack: { stt: true, llm: true, ocr: true },
  features: {
    scribe: OK,
    assistant: OK,
    ocr: OK,
    ocr_rx: OK,
    ocr_lab: OK,
    ocr_imaging: OK,
    ocr_attachments: OK,
    ocr_inbox: OK,
    ocr_record: OK,
    schedule_generator: OK,
    voiceprints: OK,
  },
};

interface AiCapabilitiesState {
  capabilities: AiCapabilities;
  loaded: boolean;
  refresh: () => Promise<void>;
}

export const useAiCapabilitiesStore = create<AiCapabilitiesState>((set) => ({
  capabilities: STATIC_FALLBACK,
  loaded: false,
  refresh: async () => {
    try {
      const fresh = await aiCapabilitiesApi.getMine();
      set({ capabilities: fresh, loaded: true });
    } catch {
      // Leave the fail-open fallback in place on a transient blip; the
      // call-time error path degrades any feature that's actually down.
      set({ loaded: true });
    }
  },
}));

/**
 * React hook — re-renders when the feature's availability flips. Returns
 * the full `{available, reason}` so callers can pick copy per reason.
 */
export function useAiAvailability(feature: AiFeatureKey): AiFeatureStatus {
  return useAiCapabilitiesStore(
    (s) => s.capabilities.features[feature] ?? STATIC_FALLBACK.features[feature],
  );
}

/** Imperatively re-pull (e.g. after a call-time AI_UNAVAILABLE error). */
export function refreshAiCapabilities(): void {
  void useAiCapabilitiesStore.getState().refresh();
}

/**
 * Mix-by-reason presentation policy. Translates a feature's availability
 * into how its affordance should render:
 *  - `disabled` / `role_forbidden` → hide entirely (not on this plan / role)
 *  - `quota_exceeded`              → show, disabled, with an upgrade nudge
 *  - `unavailable`                 → show, disabled, "temporarily down"
 *  - `no_ai_seat`                  → hide entirely (records-only seat)
 *  - `ok`                          → show, enabled
 */
export interface AiAffordance {
  show: boolean;
  enabled: boolean;
  reason: AiUnavailableReason;
  /** Short clinician-facing copy for the disabled state (null when enabled/hidden). */
  message: string | null;
  /** True when the right CTA is "upgrade / enable extra usage". */
  upsell: boolean;
}

export function aiAffordance(status: AiFeatureStatus): AiAffordance {
  switch (status.reason) {
    case 'ok':
      return { show: true, enabled: true, reason: 'ok', message: null, upsell: false };
    case 'quota_exceeded':
      return {
        show: true,
        enabled: false,
        reason: 'quota_exceeded',
        message: 'Out of AI usage for this period — upgrade or enable extra usage to continue.',
        upsell: true,
      };
    case 'unavailable':
      return {
        show: true,
        enabled: false,
        reason: 'unavailable',
        message: 'AI is temporarily unavailable. You can still work manually.',
        upsell: false,
      };
    // Records-only seat: the clinic HAS AI, this clinician's seat doesn't
    // include it. Hidden rather than disabled-with-a-nudge — the fix is for
    // an owner to move them to an AI seat, so an upsell CTA would send them
    // somewhere they can't act, and a permanently-greyed button reads as
    // broken. Same treatment as `role_forbidden`, for the same reason.
    case 'no_ai_seat':
    case 'disabled':
    case 'role_forbidden':
    default:
      return {
        show: false,
        enabled: false,
        reason: status.reason,
        message: null,
        upsell: false,
      };
  }
}

/** Convenience hook: availability + presentation policy in one call. */
export function useAiAffordance(feature: AiFeatureKey): AiAffordance {
  return aiAffordance(useAiAvailability(feature));
}
